import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
// Removed static import - now using dynamic import
import { ExportPayload, FileFormat, ExportColumn, TcsTdsOptionValue } from '../types';
import { useLoadingModal } from '../../../components';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';

interface UseExportsProps {
  columns: ExportColumn[];
  action: 'SALESEXPORT' | 'TDSEXPORT' | 'TCSEXPORT' | 'POSEXPORT' | 'PURCHASEEXPORT' | 'CREDITEXPORT' | 'DEBITEXPORT';
  options?: { tdstcsoption?: TcsTdsOptionValue };
}

export const useExports = ({ columns, action, options }: UseExportsProps) => {
  console.log('useExports initialized with action:', action);

  const [selected, setSelected] = useState<string[]>(columns.map((col) => col.accessor));
  const [selectedModule, setSelectedModule] = useState<FileFormat>('excel');
  const [dateRange, setDateRange] = useState<[Date, Date] | null>(null);
  const [errors, setErrors] = useState({ format: false, dateRange: false, tdstcsoption: false });
  const {
    status: exportStatus,
    isOpen,
    executeWithLoading,
    close,
    downloadCallback,
    showEmptyExport,
    showLoading,
    showSuccess,
    showError,
  } = useLoadingModal();

  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const startDate = params.get('startDate');
  const endDate = params.get('endDate');

  console.log('URL params - startDate:', startDate, 'endDate:', endDate);
  console.log('Current location:', location.pathname, location.search);
  const { startyear, endyear } = useSelector((state: any) => state.userPrefrenceandReducer?.financialYear || {});

  useEffect(() => {
    if (startDate && endDate) {
      const start = new Date(Number(startDate));
      const end = new Date(Number(endDate));
      setDateRange([start, end]);
    }
  }, [startDate, endDate]);

  const handleChange = (accessor: string) => {
    setSelected((prev) => (prev.includes(accessor) ? prev.filter((item) => item !== accessor) : [...prev, accessor]));
  };

  const handleSelectAll = (columns: ExportColumn[]) => {
    const allAccessors = columns.map((col) => col.accessor);
    setSelected((prev) => (prev.length === allAccessors.length ? [] : allAccessors));
  };

  const filename = (startyear: string, endyear: string, dateRange: [Date, Date] | null, action: string) => {
    let dateToday = dayjs(Date.now()).format('DD-MM-YYYY');
    const startYear = startyear ? startyear : '';
    const endYear = endyear ? endyear : '';
    let fileType = action.replace(/EXPORT$/, '_export').toLowerCase();

    if (action === 'TCSEXPORT' || action === 'TDSEXPORT') {
      const option = params.get('option')?.toUpperCase() || '';
      const tdsTcsOption = option.includes('PAY') ? 'payable' : 'receivable';
      fileType = action.replace(/EXPORT$/, `${tdsTcsOption}_export`).toLowerCase();
    }

    return `${fileType}_${startYear}-${endYear.toString().slice(-2)}FY_${dateToday}`;
  };

  const handleDateRangeChange = (value: [Date, Date] | null) => {
    setDateRange(value);
    if (value) {
      const [start, end] = value;
      const startTimestamp = Math.floor(start.getTime());
      const endTimestamp = Math.floor(end.getTime());

      const newParams = new URLSearchParams(location.search);
      newParams.set('startDate', startTimestamp.toString());
      newParams.set('endDate', endTimestamp.toString());

      navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });
      setErrors((prev) => ({ ...prev, dateRange: false }));
    } else {
      setErrors((prev) => ({ ...prev, dateRange: true }));
      setDateRange(null);
    }
  };

  const handleModuleChange = (value: FileFormat | undefined) => {
    if (value) {
      setSelectedModule(value);
      setErrors((prev) => ({ ...prev, format: false }));
    }
  };

  const handleExport = async () => {
    // Check if TDS/TCS option is required and empty
    const isTdsTcsExport = action === 'TDSEXPORT' || action === 'TCSEXPORT';
    const isTdsTcsOptionEmpty =
      isTdsTcsExport && (!options?.tdstcsoption || options.tdstcsoption === null || options.tdstcsoption === undefined);

    const newErrors = {
      format: !selectedModule,
      dateRange: !startDate || !endDate,
      tdstcsoption: isTdsTcsOptionEmpty,
    };

    setErrors(newErrors);

    if (newErrors.format || newErrors.dateRange || newErrors.tdstcsoption) {
      return;
    }

    try {
      showLoading();

      const [{ defaultExportStyles }, { validateExportPayload }, exportService] = await Promise.all([
        import('../data/exportData'),
        import('../types'),
        import('../services/ExportService'),
      ]);

      const { getExportData, downloadFile } = exportService;

      const payload: ExportPayload = {
        action,
        fromDate: Number(startDate!),
        toDate: Number(endDate!),
        fileType: selectedModule!,
        formatting: {
          headerBg: '#E6CED7',
          bodyBg: '',
          styles: defaultExportStyles,
          defaultHeaderStyle: 'header',
          defaultCellStyle: 'body',
        },
        columns: columns.filter((col) => selected.includes(col.accessor)),
      };

      // Add options if provided
      if (options) {
        payload.options = options;
      }

      console.log('Export Debug Info:');
      console.log('Action:', action);
      console.log('Options:', options);
      console.log('Payload:', payload);

      // Validate payload before sending
      const validationErrors = validateExportPayload(payload);
      if (validationErrors.length > 0) {
        const errorMessage = validationErrors.map((err) => `${err.field}: ${err.message}`).join('\n');
        throw new Error(`Export validation failed:\n${errorMessage}`);
      }

      const result = await getExportData(payload, filename(startyear, endyear, dateRange, payload.action));

      if (result.isEmptyExport) {
        // Show empty export modal with download option
        showEmptyExport(() => {
          downloadFile(result.blob, result.filename);
        });
        // Don't call showSuccess() - let the empty export modal handle the UI
      } else {
        // Auto-download as usual and show success
        downloadFile(result.blob, result.filename);
        showSuccess();
      }
    } catch (error) {
      showError();
      throw error;
    }
  };

  return {
    selected,
    selectedModule,
    setSelectedModule: handleModuleChange,
    startDate,
    endDate,
    dateRange,
    errors,
    exportStatus,
    isOpen,
    downloadCallback,
    handleChange,
    handleSelectAll,
    handleDateRangeChange,
    handleExport,
    handleCloseModal: close,
  };
};
